import React from "react";
import "./TemplateSettingsPanel.css";
import {
  customTemplateSections,
  fontOptions,
} from "./templateSettingsConfig";

const TemplateCard = ({ template, isSelected, onSelect }) => (
  <button
    type="button"
    className={`template-settings-card ${isSelected ? "is-selected" : ""}`}
    onClick={() => onSelect(template.id)}
  >
    <div className="template-settings-swatches">
      <span
        className="template-settings-swatch"
        style={{ backgroundColor: template.colors.heading_color }}
      />
      <span
        className="template-settings-swatch"
        style={{ backgroundColor: template.colors.vertical_line }}
      />
      <span
        className="template-settings-swatch"
        style={{ backgroundColor: template.colors.slide_background }}
      />
    </div>
    <span className="template-settings-card-title">{template.name}</span>
  </button>
);

const CustomTemplateCard = ({ isSelected, onSelect }) => (
  <button
    type="button"
    className={`template-settings-card template-settings-card-custom ${isSelected ? "is-selected" : ""}`}
    onClick={() => onSelect("custom")}
  >
    <div className="template-settings-custom-icon">
      <i className="bi bi-sliders2"></i>
    </div>
    <span className="template-settings-card-title">Custom Template</span>
  </button>
);

const ColorField = ({ label, value, onChange }) => (
  <div className="template-settings-color-field">
    <label className="template-settings-label mb-0">{label}</label>
    <input
      type="color"
      className="form-control form-control-color border-0 p-0"
      value={value}
      onChange={onChange}
      title={`Choose ${label}`}
    />
  </div>
);

const CustomColorSection = ({ section, themeSettings, onColorChange }) => (
  <>
    <h6 className="template-settings-subtitle">
      <i className={`bi ${section.icon} me-2`}></i>
      {section.title}
    </h6>
    <div className="row g-3">
      {section.fields.map((field) => (
        <div className={section.columns} key={field.key}>
          <ColorField
            label={field.label}
            value={themeSettings[field.key]}
            onChange={(event) => onColorChange(field.key, event.target.value)}
          />
        </div>
      ))}
    </div>
  </>
);

const BrandingSection = ({
  themeSettings,
  logoPreviewUrl,
  onLogoUpload,
  onLogoRemove,
  onLogoChange,
  onToggleBranding,
  logoUploadInputId,
}) => (
  <div className="row mb-4 align-items-center">
    <div className="col-md-7">
      <label className="template-settings-label template-settings-label-strong mb-1">
        Brand Logo (Landscape Only)
      </label>
      <div className="template-settings-logo-panel">
        <div className="template-settings-logo-preview">
          {logoPreviewUrl ? (
            <img
              src={logoPreviewUrl}
              alt="Logo"
              className="template-settings-logo-image"
            />
          ) : (
            <div className="text-center">
              <i className="bi bi-image refresh-prompts template-settings-empty-icon"></i>
              <div className="refresh-prompts template-settings-empty-text">
                No Logo
              </div>
            </div>
          )}
        </div>
        <div className="d-flex flex-column gap-2 flex-grow-1">
          <div className="d-flex gap-2">
            <button
              type="button"
              className="btn btn-sm btn-primary d-flex align-items-center gap-1"
              onClick={onLogoUpload}
            >
              <i className="bi bi-upload"></i>Upload
            </button>
            <button
              type="button"
              className="btn refresh-prompts d-flex align-items-center gap-1 border"
              onClick={onLogoRemove}
              disabled={!logoPreviewUrl}
            >
              <i className="bi bi-trash refresh-prompts"></i><span className="refresh-prompts"> Remove</span>
            </button>
          </div>
          <input
            id={logoUploadInputId}
            type="file"
            className="d-none"
            accept="image/*"
            onChange={onLogoChange}
          />
          <div className="refresh-prompts template-settings-hint">
            <i className="bi bi-info-circle me-1"></i>
            Supports JPG, PNG, SVG (Max 2MB)
          </div>
        </div>
      </div>
    </div>
    <div className="col-md-3 d-flex align-items-center justify-content-end">
      <div className="form-check form-switch custom-switch">
        <input
          className="form-check-input"
          type="checkbox"
          id="enableLogo"
          checked={themeSettings.enable_logo}
          onChange={(event) => onToggleBranding(event.target.checked)}
          style={{ width: "2.5rem", height: "1.25rem" }}
        />
        <label
          className="form-check-label template-settings-label template-settings-label-strong ms-2"
          htmlFor="enableLogo"
        >
          Enable Branding
        </label>
      </div>
    </div>
  </div>
);

const TypographySection = ({ themeSettings, onFieldChange }) => (
  <>
    <h6 className="template-settings-subtitle">
      <i className="bi bi-textarea-t me-2"></i>
      Typography
    </h6>
    <div className="row g-3">
      <div className="col-md-4">
        <label className="template-settings-label template-settings-label-strong">
          Primary Font
        </label>
        <select
          className="form-select"
          value={themeSettings.primary_font}
          onChange={(event) => onFieldChange("primary_font", event.target.value)}
        >
          {fontOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="template-settings-label template-settings-label-strong">
          Header Font
        </label>
        <select
          className="form-select"
          value={themeSettings.header_font}
          onChange={(event) => onFieldChange("header_font", event.target.value)}
        >
          {fontOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
      <div className="col-md-4">
        <label className="template-settings-label template-settings-label-strong">
          Footer Font
        </label>
        <select
          className="form-select"
          value={themeSettings.footer_font}
          onChange={(event) => onFieldChange("footer_font", event.target.value)}
        >
          {fontOptions.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </div>
    </div>
  </>
);

const FooterSection = ({ themeSettings, onFieldChange, onToggleChange }) => (
  <>
    <h6 className="template-settings-subtitle">
      <i className="bi bi-layout-text-sidebar-reverse me-2"></i>
      Footer Settings
    </h6>
    <div className="row g-3">
      <div className="col-md-2">
        <label className="template-settings-label template-settings-label-strong">
          Footer Font Size
        </label>
        <input
          type="number"
          className="form-control"
          value={themeSettings.footer_font_size}
          onChange={(event) =>
            onFieldChange(
              "footer_font_size",
              parseInt(event.target.value, 10) || 0,
            )
          }
        />
      </div>
      <div className="col-md-6">
        <label className="template-settings-label template-settings-label-strong">
          Confidential Text
        </label>
        <input
          type="text"
          className="form-control"
          value={themeSettings.confidential_text}
          onChange={(event) =>
            onFieldChange("confidential_text", event.target.value)
          }
        />
      </div>
      <div className="col-md-2 d-flex align-items-center mt-4">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="enableFooter"
            checked={themeSettings.enable_footer}
            onChange={(event) => onToggleChange("enable_footer", event.target.checked)}
          />
          <label
            className="form-check-label template-settings-label template-settings-label-strong small"
            htmlFor="enableFooter"
          >
            Enable Footer
          </label>
        </div>
      </div>
      <div className="col-md-2 d-flex align-items-center mt-4">
        <div className="form-check form-switch">
          <input
            className="form-check-input"
            type="checkbox"
            id="enableConfidential"
            checked={themeSettings.enable_confidential}
            onChange={(event) =>
              onToggleChange("enable_confidential", event.target.checked)
            }
          />
          <label
            className="form-check-label template-settings-label template-settings-label-strong small"
            htmlFor="enableConfidential"
          >
            Enable Confidential
          </label>
        </div>
      </div>
    </div>
  </>
);

const TemplateSettingsPanel = ({
  isLoadingTheme,
  templates,
  selectedTemplate,
  themeSettings,
  templateColors,
  logoPreviewUrl,
  onTemplateSelect,
  onThemeFieldChange,
  onThemeColorChange,
  onLogoUpload,
  onLogoRemove,
  onLogoChange,
  onBrandingToggle,
  onTemplateSave,
  isSavingThemeOnly,
}) => {
  const isCustomTemplate = selectedTemplate === "custom";

  return (
    <div className="theme-settings-container template-settings-panel p-4 mb-4 border rounded shadow-sm position-relative">
      {isLoadingTheme && (
        <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center bg-white bg-opacity-75 rounded template-settings-loader">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      <h6 className="template-settings-title">
        <i className="bi bi-layout-text-window-reverse me-2"></i>
        Choose Template
      </h6>
      <p className="template-settings-description">
        Pick one of the four preset templates.
      </p>

      <div className="template-settings-grid mb-4">
        {templates.map((template) => (
          <TemplateCard
            key={template.id}
            template={template}
            isSelected={selectedTemplate === template.id}
            onSelect={onTemplateSelect}
          />
        ))}
        {/* <CustomTemplateCard
          isSelected={isCustomTemplate}
          onSelect={onTemplateSelect}
        /> */}
      </div>

      <BrandingSection
        themeSettings={themeSettings}
        logoPreviewUrl={logoPreviewUrl}
        onLogoUpload={onLogoUpload}
        onLogoRemove={onLogoRemove}
        onLogoChange={onLogoChange}
        onToggleBranding={onBrandingToggle}
        logoUploadInputId="logoUploadInput"
      />

      {/* {isCustomTemplate && (
        <div className="template-settings-custom-sections">
          {customTemplateSections.map((section) => (
            <div className="mb-4" key={section.id}>
              <CustomColorSection
                section={section}
                themeSettings={templateColors}
                onColorChange={onThemeColorChange}
              />
            </div>
          ))}
          <div className="mb-4">
            <TypographySection
              themeSettings={themeSettings}
              onFieldChange={onThemeFieldChange}
            />
          </div>
          <FooterSection
            themeSettings={themeSettings}
            onFieldChange={onThemeFieldChange}
            onToggleChange={onThemeFieldChange}
          />
        </div>
      )} */}

      <div className="d-flex justify-content-end mt-3">
        <button
          type="button"
          className="custom-btn"
          onClick={onTemplateSave}
          disabled={isSavingThemeOnly}
        >
          {isSavingThemeOnly ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving Template...
            </>
          ) : (
            "Save Template Settings"
          )}
        </button>
      </div>
    </div>
  );
};

export default TemplateSettingsPanel;
